import { Link } from "react-router";

import { Chip, ChipRow } from "../lib/Chip.tsx";
import { InitialsTile } from "../lib/InitialsTile.tsx";
import { Tag } from "../lib/Tag.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { GALLERY_AGENTS_COPY } from "./galleryAgents.constants.ts";
import type { AgentGroup, OfferRow, PlacementRow, SellerGroup } from "./galleryAgents.model.ts";
import { useGalleryAgentsController } from "./useGalleryAgentsController.ts";
import "./gallery.css";
import "./galleryAgents.css";

/**
 * The gallery's agents, both directions.
 *
 * Replaces the reuse of the AGENT portal's `<Representations>`, which answered
 * the opposite question — the works other people had asked this firm to
 * represent — and whose empty state told a gallery that "a collector names their
 * agent from a work's record".
 *
 * Built out of the same objects every other list in ONDO is: the `TileRow` the
 * dashboards open with, the `ChipRow` Inventory filters by, the `InitialsTile`
 * in the rail, the `WorkThumb` on every work in the product and the
 * `<button aria-expanded>` panel head the agent's own client list uses. There is
 * nothing bespoke here, because there is nothing about a gallery's agents that
 * is a different KIND of thing from a collector's.
 *
 * It carries no title of its own: the portal header above it has one.
 */
export function GalleryAgents({ workBase }: { workBase: string }) {
  const { view, chooseSide, toggleGroup, recall, recalling } =
    useGalleryAgentsController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <p className="gallery__note gallery__note--error" role="alert">
        {view.message}
      </p>
    );
  }

  return (
    <>
      <section className="gallery__lead">
        <p>
          <strong>{GALLERY_AGENTS_COPY.lead}</strong> {GALLERY_AGENTS_COPY.leadDetail}
        </p>
      </section>

      <Primer />

      <TileRow tiles={view.tiles} />

      <div className="gallery__toolbar">
        <ChipRow label={GALLERY_AGENTS_COPY.sideLabel}>
          <Chip pressed={view.side === "buyer"} onClick={() => chooseSide("buyer")}>
            {view.buyerLabel}
          </Chip>
          <Chip pressed={view.side === "seller"} onClick={() => chooseSide("seller")}>
            {view.sellerLabel}
          </Chip>
        </ChipRow>
      </div>

      {view.empty ? (
        <p className="gallery__note">{view.empty}</p>
      ) : view.side === "buyer" ? (
        view.buyers.map((group) => (
          <BuyerPanel
            key={group.key}
            group={group}
            onToggle={() => toggleGroup(group.key)}
            onRecall={recall}
            recalling={recalling}
          />
        ))
      ) : (
        view.sellers.map((group) => (
          <SellerPanel
            key={group.key}
            group={group}
            onToggle={() => toggleGroup(group.key)}
          />
        ))
      )}
    </>
  );
}

/**
 * The standing explanation, closed.
 *
 * A native `<details>` rather than a button and a piece of state: it is a
 * disclosure and nothing else, it is open-able before hydration, and the browser
 * already announces it correctly. The one thing the markup must not do is
 * promise a choice — see `GALLERY_AGENTS_COPY.primer`.
 */
function Primer() {
  return (
    <details className="gallery__primer">
      <summary className="gallery__primer-head">
        <span className="gallery__primer-body">
          <span className="gallery__primer-title">{GALLERY_AGENTS_COPY.primerTitle}</span>
          <span className="gallery__primer-sub">{GALLERY_AGENTS_COPY.primerSubtitle}</span>
        </span>
      </summary>

      <div className="gallery__primer-open">
        {GALLERY_AGENTS_COPY.primer.map((section) => (
          <section key={section.key} className="gallery__primer-section">
            <h3 className="gallery__primer-heading">{section.heading}</h3>
            <p className="gallery__primer-text">{section.body}</p>
          </section>
        ))}
      </div>
    </details>
  );
}

function BuyerPanel({
  group,
  onToggle,
  onRecall,
  recalling,
}: {
  group: AgentGroup;
  onToggle: () => void;
  onRecall: (workId: string, mandateId: string) => void;
  recalling: string | null;
}) {
  return (
    <section className="gallery__agent">
      <button
        type="button"
        className="gallery__agent-head"
        aria-expanded={group.open}
        onClick={onToggle}
      >
        <InitialsTile initials={group.initials} className="gallery__agent-initials" />
        <span className="gallery__agent-body">
          <span className="gallery__agent-name">
            {group.name}
            {group.verified && (
              // `gallery__agent-kyc` was the class name here and styled nothing
              // at all — a dead hook whose name still said KYC, which is what
              // somebody grepping for the word would have found after OND-75
              // changed the copy above it.
              <Tag tone="verified">{GALLERY_AGENTS_COPY.verified}</Tag>
            )}
          </span>
          <span className="gallery__agent-meta">{group.meta}</span>
        </span>
        <span className="gallery__agent-chevron" aria-hidden="true">
          {group.open ? "⌄" : "›"}
        </span>
        <span className="gallery__agent-action">
          {group.open ? GALLERY_AGENTS_COPY.collapse : GALLERY_AGENTS_COPY.expand}
        </span>
      </button>

      {group.open && (
        <>
          <p className="gallery__agent-label">{GALLERY_AGENTS_COPY.worksSent}</p>
          <ul className="gallery__placements">
            {group.rows.map((row) => (
              <PlacementRowView
                key={row.mandateId}
                row={row}
                onRecall={onRecall}
                recalling={recalling}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function PlacementRowView({
  row,
  onRecall,
  recalling,
}: {
  row: PlacementRow;
  onRecall: (workId: string, mandateId: string) => void;
  recalling: string | null;
}) {
  return (
    <li className="gallery__placement">
      <span className="gallery__placement-thumb">
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
      </span>

      <span className="gallery__placement-body">
        <span className="gallery__placement-title">{row.title}</span>
        <span className="gallery__placement-meta">{row.attribution}</span>
        {/* The date and the gallery's own words. There is no status pill, and
            the model says why: what an agent is doing with a work is the agent's
            report, and nothing in this registry observes it. */}
        <span className="gallery__placement-sent">{row.sent}</span>
      </span>

      <span className="gallery__placement-actions">
        {row.canEnd && (
          <button
            type="button"
            className="gallery__placement-recall"
            disabled={recalling === row.mandateId}
            onClick={() => onRecall(row.workId, row.mandateId)}
          >
            {GALLERY_AGENTS_COPY.recall}
          </button>
        )}
        <Link className="gallery__placement-open" to={row.href}>
          {GALLERY_AGENTS_COPY.view}
        </Link>
      </span>
    </li>
  );
}

function SellerPanel({ group, onToggle }: { group: SellerGroup; onToggle: () => void }) {
  return (
    <section className="gallery__agent">
      <button
        type="button"
        className="gallery__agent-head"
        aria-expanded={group.open}
        onClick={onToggle}
      >
        <InitialsTile initials={group.initials} className="gallery__agent-initials" />
        <span className="gallery__agent-body">
          <span className="gallery__agent-name">{group.name}</span>
          <span className="gallery__agent-meta">{group.meta}</span>
        </span>
        <span className="gallery__agent-chevron" aria-hidden="true">
          {group.open ? "⌄" : "›"}
        </span>
        <span className="gallery__agent-action">
          {group.open ? GALLERY_AGENTS_COPY.collapse : GALLERY_AGENTS_COPY.expand}
        </span>
      </button>

      {group.open && (
        <>
          <p className="gallery__agent-label">{GALLERY_AGENTS_COPY.offered}</p>
          <ul className="gallery__placements">
            {group.rows.map((row) => (
              <OfferRowView key={row.consignmentId} row={row} />
            ))}
          </ul>
          {/* Accept and Decline are on Inventory, where the asking price and the
              terms are. A gallery must not be asked to commit to holding
              somebody's property from a screen that shows neither. */}
          <p className="gallery__agent-note">{GALLERY_AGENTS_COPY.answerAt}</p>
        </>
      )}
    </section>
  );
}

function OfferRowView({ row }: { row: OfferRow }) {
  return (
    <li className="gallery__placement">
      <span className="gallery__placement-thumb">
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
      </span>
      <span className="gallery__placement-body">
        <span className="gallery__placement-title">{row.title}</span>
        <span className="gallery__placement-meta">{row.attribution}</span>
      </span>
      <span className="gallery__placement-actions">
        <Link className="gallery__placement-open" to={row.href}>
          {GALLERY_AGENTS_COPY.view}
        </Link>
      </span>
    </li>
  );
}
